import { ArrowDropDown } from '@emotion-icons/remix-line';
import styled from '@emotion/styled';
import { useState } from 'react';
import ClickOutside from './ClickOutside';

interface CTAOption {
  label: string;
  onClick: () => void;
  icon?: React.ReactNode;
  variant?: 'default' | 'danger';
}

interface DropdownButtonProps {
  buttonText: string;
  options: CTAOption[];
  size?: 'auto' | 'full' | 'md' | 'smd' | 'sm' | 'lg';
  variant?: 'primary' | 'secondary' | 'danger' | 'success';
  disabled?: boolean;
  className?: string;
}

const DropdownWrapper = styled.div`
  position: relative;
  display: inline-block;
`;

const StyledButton = styled.button<{
  size?: string;
  variant?: string;
  disabled?: boolean;
}>`
  height: 44px;
  border-radius: 8px;
  padding: 0 10px;
  font-family: 'Poppins', sans-serif;
  font-size: 16px;
  font-weight: 500;
  line-height: 30px;
  text-align: center;
  user-select: none;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  transition:
    background-color 0.3s,
    filter 0.2s ease;
  overflow: hidden;
  white-space: nowrap;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  width: ${({ size = 'auto' }) => (size === 'full' ? '100%' : 'auto')};

  background-color: ${({ variant = 'primary', disabled }) =>
    disabled
      ? '#F2F2F3'
      : variant === 'primary'
        ? 'var(--prim4)'
        : variant === 'secondary'
          ? 'var(--white)'
          : variant === 'danger'
            ? '#ff4d4f'
            : variant === 'success'
              ? '#52c41a'
              : 'var(--prim4)'};

  color: ${({ variant = 'primary', disabled }) =>
    disabled
      ? '#7D7986'
      : variant === 'primary' || variant === 'danger' || variant === 'success'
        ? 'var(--white)'
        : 'var(--t-black)'};

  border: 1px solid;
  border-color: ${({ variant = 'primary', disabled }) =>
    disabled
      ? '#F2F2F3'
      : variant === 'primary'
        ? 'var(--prim4)'
        : variant === 'secondary'
          ? '#6c668540'
          : variant === 'danger'
            ? '#ff4d4f'
            : variant === 'success'
              ? '#52c41a'
              : 'var(--prim4)'};

  &:hover {
    filter: ${({ disabled }) => (disabled ? 'none' : 'brightness(90%)')};
  }

  &:active {
    filter: ${({ disabled }) => (disabled ? 'none' : 'brightness(80%)')};
  }

  @media only screen and (min-width: 800px) {
    height: ${({ size = 'auto' }) =>
      size === 'sm'
        ? '40px'
        : size === 'smd'
          ? '47px'
          : size === 'md'
            ? '52px'
            : size === 'lg'
              ? '64px'
              : '56px'};

    font-size: ${({ size = 'auto' }) =>
      size === 'sm'
        ? '16px'
        : size === 'smd'
          ? '18px'
          : size === 'md'
            ? '16px'
            : size === 'lg'
              ? '22px'
              : '20px'};

    padding: ${({ size = 'auto' }) =>
      size === 'sm'
        ? '0 16px'
        : size === 'smd'
          ? '6px 24px'
          : size === 'md'
            ? '10px 18px'
            : size === 'lg'
              ? '16px 24px'
              : '13px 22px'};
  }
`;

const DropdownIcon = styled.span<{ open: boolean }>`
  display: flex;
  align-items: center;
  transform: ${props => (props.open ? 'rotate(180deg)' : 'rotate(0deg)')};
  transition: transform 0.2s ease;
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  min-width: 200px;
  background: white;
  border: 1px solid var(--border1);
  border-radius: 8px;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  z-index: 1000;
`;

const DropdownList = styled.ul`
  list-style: none;
  padding: 6px;
  margin: 0;
`;

const DropdownItem = styled.li<{ variant?: string }>`
  border-radius: 4px;
  padding: 12px 16px;
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: ${props => (props.variant === 'danger' ? '#ff4d4f' : 'var(--raisin-black)')};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 4px 0;
  transition: background-color 0.2s ease;

  &:first-of-type {
    margin-top: 0;
  }

  &:last-of-type {
    margin-bottom: 0;
  }

  &:hover {
    background: ${props => (props.variant === 'danger' ? '#fff1f0' : 'var(--neut7)')};
  }
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  width: 16px;
  height: 16px;
`;

const DropdownButton: React.FC<DropdownButtonProps> = ({
  buttonText,
  options,
  size = 'auto',
  variant = 'primary',
  disabled = false,
  className = '',
}) => {
  const [open, setOpen] = useState(false);

  const handleOptionClick = (onClick: () => void) => {
    setOpen(false);
    onClick();
  };

  return (
    <ClickOutside onClose={() => setOpen(false)}>
      <DropdownWrapper className={className}>
        <StyledButton
          size={size}
          variant={variant}
          disabled={disabled}
          onClick={() => !disabled && setOpen(!open)}
        >
          {buttonText}
          <DropdownIcon open={open}>
            <ArrowDropDown size={24} />
          </DropdownIcon>
        </StyledButton>

        {open && !disabled && (
          <DropdownMenu>
            <DropdownList>
              {options.map((option, index) => (
                <DropdownItem
                  key={`${option.label}-${index}`}
                  variant={option.variant}
                  onClick={() => handleOptionClick(option.onClick)}
                >
                  {option.icon && <IconWrapper>{option.icon}</IconWrapper>}
                  {option.label}
                </DropdownItem>
              ))}
            </DropdownList>
          </DropdownMenu>
        )}
      </DropdownWrapper>
    </ClickOutside>
  );
};

export default DropdownButton;
